if (!customElements.get('reviewsio-all-reviews')) {
  customElements.define('reviewsio-all-reviews', class ReviewsioAllReviews extends HTMLElement {
    connectedCallback() {
      this.store = this.dataset.store;
      this.perPage = parseInt(this.dataset.perPage, 10) || 12;
      this.minRating = parseInt(this.dataset.minRating, 10) || 1;
      this.hiddenCount = Math.max(0, parseInt(this.dataset.hiddenCount, 10) || 0);
      this.summaryTemplate = this.dataset.summary || '';
      this.page = 0;
      this.total = 0;
      this.loading = false;
      // Never reset: a request left over from before a re-insert must not match a new id.
      this.requestId = this.requestId || 0;
      this.cards = [];
      this.placed = 0;
      // Kept across a re-insert: the summary and rating counts are store-wide and already shown.
      this.summaryDone = this.summaryDone || false;

      this.summaryEl = this.querySelector('[data-rio-summary]');
      this.listEl = this.querySelector('[data-rio-list]');
      this.moreEl = this.querySelector('[data-rio-more]');
      this.statusEl = this.querySelector('[data-rio-status]');
      this.ratingEl = this.querySelector('[data-rio-rating]');
      this.photosEl = this.querySelector('[data-rio-photos]');
      this.sortEl = this.querySelector('[data-rio-sort]');

      // Filters stay disabled until the first (unfiltered) response has fed the summary and counts.
      // The browser may restore old filter values on reload, so clear them before that request.
      if (!this.summaryDone) {
        this.ratingEl.value = '';
        this.photosEl.checked = false;
      }
      this.setFiltersDisabled(!this.summaryDone);
      this.listEl.textContent = '';

      this._onClick = (e) => {
        if (e.target.closest('[data-rio-more]')) this.loadMore();
      };
      this._onChange = (e) => {
        if (e.target.closest('[data-rio-filter]')) this.applyFilters();
      };
      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onChange);

      this._mq = window.matchMedia('(min-width: 768px)');
      this._onMq = () => this.layout(true);
      this._mq.addEventListener('change', this._onMq);

      this.start();
    }

    // A rating below the section's minimum is still offered in the filter. Picking it shows at
    // most hiddenCount of those reviews (one request, no "load more") and the hidden-rating message.
    isHiddenRating() {
      const rating = parseInt(this.ratingEl.value, 10);
      return rating > 0 && rating < this.minRating;
    }

    start() {
      if (this.isHiddenRating() && !this.hiddenCount) {
        this.moreEl.hidden = true;
        this.showHiddenMessage();
        return;
      }
      this.loadMore();
    }

    // The message can link to a page (e.g. contact); the link is a merchant setting, still checked.
    showHiddenMessage() {
      const text = (this.dataset.hiddenRating || '').replace('{rating}', this.minRating);
      const href = this.safeUrl(this.dataset.hiddenRatingLink);
      this.statusEl.textContent = '';
      if (!text) return;
      if (!href) {
        this.statusEl.textContent = text;
        return;
      }
      const link = this.el('a', 'reviewsio-all-reviews__status-link', text);
      link.href = href;
      this.statusEl.appendChild(link);
    }

    disconnectedCallback() {
      this.requestId++;
      if (this.controller) this.controller.abort();
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onChange);
      if (this._mq) this._mq.removeEventListener('change', this._onMq);
    }

    endpoint(page) {
      const hidden = this.isHiddenRating();
      let url = 'https://api.reviews.io/timeline/data?type=product_review'
        + '&store=' + encodeURIComponent(this.store)
        + '&sort=' + encodeURIComponent(this.sortEl.value)
        + '&page=' + page
        + '&per_page=' + (hidden ? this.hiddenCount : this.perPage)
        + '&include_product_images=1&widget=polaris';
      // minRating (camelCase) filters on the server, so paging and the rating counts follow it;
      // the stats used by the summary stay store-wide. A hidden rating is asked for without it.
      if (this.minRating > 1 && !hidden) url += '&minRating=' + this.minRating;
      if (this.ratingEl.value) url += '&rating=' + encodeURIComponent(this.ratingEl.value);
      if (this.photosEl.checked) url += '&with=images';
      return url;
    }

    setFiltersDisabled(disabled) {
      [this.ratingEl, this.photosEl, this.sortEl].forEach((el) => {
        el.disabled = disabled;
      });
    }

    applyFilters() {
      this.requestId++;
      if (this.controller) this.controller.abort();
      this.loading = false;
      this.page = 0;
      this.total = 0;
      this.cards = [];
      this.placed = 0;
      this.listEl.textContent = '';
      this.moreEl.hidden = true;
      this.moreEl.disabled = false;
      this.statusEl.textContent = '';
      this.start();
    }

    // Shown ratings come from the response buckets (with counts). Ratings below the minimum are
    // filtered out of those buckets by the API, so they are listed without a count.
    renderRatingOptions(buckets) {
      if (this.ratingOptionsDone || !Array.isArray(buckets)) return;
      this.ratingOptionsDone = true;
      const counts = {};
      buckets.forEach((bucket) => {
        counts[bucket.key] = bucket.doc_count;
      });
      for (let key = 5; key >= 1; key--) {
        const hidden = key < this.minRating;
        if (!hidden && !(counts[key] > 0)) continue;
        const option = document.createElement('option');
        option.value = String(key);
        option.textContent = key + ' \u2605' + (hidden ? '' : ' (' + counts[key].toLocaleString('en') + ')');
        this.ratingEl.appendChild(option);
      }
    }

    loadMore() {
      if (this.loading) return;
      // A hidden rating gets a single page of hiddenCount reviews.
      if (this.isHiddenRating() && (!this.hiddenCount || this.page > 0)) return;
      this.loading = true;
      this.moreEl.disabled = true;
      this.statusEl.textContent = this.dataset.loading || '';

      const request = ++this.requestId;
      const next = this.page + 1;
      this.controller = new AbortController();
      fetch(this.endpoint(next), { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('REVIEWS.io ' + response.status);
          return response.json();
        })
        .then((data) => {
          if (request !== this.requestId) return;
          const reviews = (data.timeline || []).map((item) => item._source).filter(Boolean);
          this.page = next;
          this.total = parseInt(data.stats && data.stats.results_count, 10) || 0;
          // The first response is unfiltered: it feeds the header summary and the rating filter counts.
          if (!this.summaryDone && data.stats) {
            this.summaryDone = true;
            this.renderSummary(data.stats);
            this.renderRatingOptions(data.aggregations && data.aggregations.ratings && data.aggregations.ratings.buckets);
            this.setFiltersDisabled(false);
          }
          reviews.forEach((review) => this.cards.push(this.buildCard(review)));
          this.layout(false);
          if (this.isHiddenRating()) {
            this.moreEl.hidden = true;
            this.showHiddenMessage();
            return;
          }
          this.statusEl.textContent = !reviews.length && next === 1 ? (this.dataset.empty || '') : '';
          this.moreEl.hidden = this.page * this.perPage >= this.total || !reviews.length;
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          if (request !== this.requestId) return;
          this.statusEl.textContent = this.dataset.error || '';
          // The button doubles as a retry: loadMore asks for the same page again.
          this.moreEl.hidden = false;
        })
        .then(() => {
          if (request !== this.requestId) return;
          this.loading = false;
          this.moreEl.disabled = false;
        });
    }

    renderSummary(stats) {
      const average = parseFloat(stats.average_rating);
      if (!(average > 0)) return;
      this.summaryEl.textContent = '';

      const score = document.createElement('span');
      score.className = 'reviewsio-all-reviews__score';
      score.textContent = average.toFixed(2);

      const text = document.createElement('span');
      text.className = 'reviewsio-all-reviews__count';
      text.textContent = this.summaryTemplate.replace('{count}', (parseInt(stats.review_count, 10) || this.total).toLocaleString('en'));

      this.summaryEl.append(score, this.buildStars(average), text);
      this.summaryEl.hidden = false;
    }

    buildStars(rating) {
      const stars = document.createElement('span');
      stars.className = 'reviewsio-all-reviews__stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', rating + ' / 5');
      const fill = document.createElement('span');
      fill.className = 'reviewsio-all-reviews__stars-fill';
      fill.style.width = Math.max(0, Math.min(100, (rating / 5) * 100)) + '%';
      fill.textContent = '\u2605\u2605\u2605\u2605\u2605';
      stars.append(document.createTextNode('\u2605\u2605\u2605\u2605\u2605'), fill);
      return stars;
    }

    formatDate(value) {
      const date = new Date(String(value || '').replace(' ', 'T') + 'Z');
      return isNaN(date) ? '' : date.toLocaleDateString('en-US');
    }

    photoUrl(image) {
      const source = 'https://d1011j0lbv5k1u.cloudfront.net/assets/' + image;
      return 'https://media.reviews.co.uk/resize/create?format=jpg&height=600&width=600&src=' + encodeURIComponent(source);
    }

    thumbUrl(url) {
      try {
        const parsed = new URL(url);
        parsed.searchParams.set('width', '120');
        return parsed.toString();
      } catch (error) {
        return url;
      }
    }

    // Only plain http(s) links are allowed (the value comes from a third-party API).
    safeUrl(value) {
      if (!value || typeof value !== 'string') return '';
      try {
        const url = new URL(value, window.location.origin);
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
      } catch (error) {
        return '';
      }
    }

    el(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text) node.textContent = text;
      return node;
    }

    buildCard(review) {
      const card = this.el('article', 'reviewsio-all-reviews__card');
      const main = this.el('div', 'reviewsio-all-reviews__main');
      const body = this.el('div', 'reviewsio-all-reviews__body');

      const head = this.el('div', 'reviewsio-all-reviews__head');
      head.appendChild(this.el('span', 'reviewsio-all-reviews__author', review.author || ''));
      body.appendChild(head);

      body.appendChild(this.el('div', 'reviewsio-all-reviews__date', this.formatDate(review.date_created)));
      body.appendChild(this.buildStars(parseFloat(review.rating) || 0));

      const comment = (review.comments || '').trim();
      if (comment) body.appendChild(this.el('p', 'reviewsio-all-reviews__text', comment));
      main.appendChild(body);

      let images = review.images;
      if (typeof images === 'string') {
        try { images = JSON.parse(images); } catch (error) { images = []; }
      }
      const photo = Array.isArray(images) && images[0] && images[0].image;
      if (photo) {
        const media = this.el('div', 'reviewsio-all-reviews__media');
        const img = document.createElement('img');
        img.src = this.photoUrl(photo);
        img.alt = '';
        img.loading = 'lazy';
        media.appendChild(img);
        main.appendChild(media);
      }
      card.appendChild(main);

      if (review.product_name) {
        const href = this.safeUrl(review.product_link);
        const product = this.el(href ? 'a' : 'div', 'reviewsio-all-reviews__product');
        if (href) product.href = href;
        if (review.product_image) {
          const thumb = document.createElement('img');
          thumb.src = this.thumbUrl(review.product_image);
          thumb.alt = '';
          thumb.loading = 'lazy';
          product.appendChild(thumb);
        }
        product.appendChild(this.el('span', 'reviewsio-all-reviews__product-name', review.product_name));
        card.appendChild(product);
      }
      return card;
    }

    // Masonry: each card goes into the currently shortest column.
    layout(reset) {
      const count = this._mq.matches ? 2 : 1;
      let columns = [].slice.call(this.listEl.children);
      if (reset || columns.length !== count) {
        this.listEl.textContent = '';
        columns = [];
        for (let i = 0; i < count; i++) {
          const column = this.el('div', 'reviewsio-all-reviews__column');
          this.listEl.appendChild(column);
          columns.push(column);
        }
        this.placed = 0;
      }
      for (; this.placed < this.cards.length; this.placed++) {
        const shortest = columns.reduce((min, column) => (column.offsetHeight < min.offsetHeight ? column : min));
        shortest.appendChild(this.cards[this.placed]);
      }
    }
  });
}
